import React, { useState, useEffect, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import {
  useGetFamilyTreeQuery,
  useCreateConversationMutation,
} from "../../../../store/slices/api";
import { SharedButton, SharedLoading } from "../../../shared";
import PersonPicker from "../../FamilyConnect/PersonPicker";

const MAX_CHARS = 600;

function Compose({ individualId, onConversationCreated }) {
  const [searchParams, setSearchParams] = useSearchParams();
  const { data: familyTreeData, isLoading: treeLoading } =
    useGetFamilyTreeQuery();
  const [createConversation, { isLoading: isSending }] =
    useCreateConversationMutation();

  const [recipient, setRecipient] = useState(null);

  // Everyone but the member: the server refuses a letter to yourself, so it
  // isn't offered.
  const recipients = useMemo(
    () => familyTreeData?.filter((p) => String(p.id) !== String(individualId)),
    [familyTreeData, individualId]
  );

  // Pre-select recipient if navigated from a profile (e.g. /messages?tab=compose&to=123)
  useEffect(() => {
    const toId = searchParams.get("to");
    if (toId && recipients && !recipient) {
      const person = recipients.find((p) => String(p.id) === String(toId));
      if (person) {
        const first = person.data.first_name || "";
        const last = person.data.last_name || "";
        const suffix = person.data.numeric_suffix || "";
        const label = [first, last, suffix].filter(Boolean).join(" ");
        setRecipient({ id: person.id, label, person });
      }
      // Clean the "to" param from the URL so it doesn't stick around. A link
      // addressed to the member themselves (an old one) just leaves "To" empty.
      if (person || String(toId) === String(individualId)) {
        searchParams.delete("to");
        setSearchParams(searchParams, { replace: true });
      }
    }
  }, [recipients, individualId, searchParams, recipient, setSearchParams]);
  const [subject, setSubject] = useState("");
  const [content, setContent] = useState("");
  const [mediaUrl, setMediaUrl] = useState(null);
  const [mediaType, setMediaType] = useState("text");
  const [error, setError] = useState("");
  const [sent, setSent] = useState(false);

  const handleImageUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const isGif = file.type === "image/gif";

    const reader = new FileReader();
    reader.onload = (event) => {
      if (isGif) {
        setMediaUrl(event.target.result);
        setMediaType("gif");
      } else {
        const img = new Image();
        img.onload = () => {
          const canvas = document.createElement("canvas");
          let width = img.width;
          let height = img.height;
          const maxSize = 400;
          if (width > height) {
            if (width > maxSize) {
              height = Math.round((height * maxSize) / width);
              width = maxSize;
            }
          } else {
            if (height > maxSize) {
              width = Math.round((width * maxSize) / height);
              height = maxSize;
            }
          }
          canvas.width = width;
          canvas.height = height;
          canvas.getContext("2d").drawImage(img, 0, 0, width, height);
          setMediaUrl(canvas.toDataURL("image/jpeg", 0.8));
          setMediaType("image");
        };
        img.src = event.target.result;
      }
    };
    reader.readAsDataURL(file);
  };

  const handleSend = async () => {
    setError("");

    if (!recipient) {
      setError("Please select a recipient.");
      return;
    }
    if (!subject.trim()) {
      setError("Please add a subject line.");
      return;
    }
    if (!content.trim()) {
      setError("Please write your letter.");
      return;
    }
    if (content.length > MAX_CHARS) {
      setError(`Letter must be ${MAX_CHARS} characters or fewer.`);
      return;
    }

    try {
      const result = await createConversation({
        sender_individual_id: individualId,
        recipient_individual_id: recipient.id,
        subject: subject.trim(),
        content: content.trim(),
        message_type: mediaType,
        media_url: mediaUrl,
      }).unwrap();

      setSent(true);
      setTimeout(() => {
        onConversationCreated(result.id);
      }, 1500);
    } catch (err) {
      setError(
        err?.data?.error || "Something went wrong sending your letter."
      );
    }
  };

  if (treeLoading) return <SharedLoading />;

  if (sent) {
    return (
      <div className="correspondence-empty">
        Your letter has been sealed and sent!
      </div>
    );
  }

  const charCount = content.length;
  const charClass =
    charCount > MAX_CHARS
      ? "char-counter-over"
      : charCount > MAX_CHARS * 0.9
      ? "char-counter-warn"
      : "char-counter-ok";

  return (
    <div className="max-w-[780px] mx-auto w-full">
      {/* Stationery card — looks like a letter on a desk */}
      <div
        className="relative rounded-[4px] p-[40px] sm:p-[48px]"
        style={{
          backgroundColor: "#fdfcf5",
          border: "1px solid #d4c26a",
          boxShadow:
            "0 1px 3px rgba(181, 160, 62, 0.08), 0 8px 24px rgba(0, 9, 28, 0.06), inset 0 0 80px rgba(181, 160, 62, 0.03)",
        }}
      >
        {/* Subtle top gold line — like a letterhead rule */}
        <div
          className="absolute top-0 left-[40px] right-[40px] h-[2px]"
          style={{
            background: "linear-gradient(90deg, transparent, #b5a03e, transparent)",
          }}
        />

        <div className="flex flex-col gap-y-[28px]">
          {/* Header */}
          <div className="text-center mb-[4px]">
            <h2
              className="text-[26px] font-display font-bold tracking-wide"
              style={{ color: "#1a3a5c" }}
            >
              Write a Letter
            </h2>
            <div className="w-[50px] h-[1px] bg-dupontGold mx-auto mt-[8px]" />
          </div>

          {/* Error message */}
          {error && (
            <div className="rounded-[8px] bg-red-50 border border-red-200 p-[12px] text-[14px] text-red-700 font-body">
              {error}
            </div>
          )}

          {/* Recipient picker */}
          <div className="flex flex-col gap-y-[6px]">
            <label className="text-[14px] font-display font-semibold uppercase tracking-wider text-dupontBlue/60">
              To
            </label>
            <PersonPicker
              familyTreeData={recipients}
              onSelect={setRecipient}
              label="Select a family member"
              value={recipient}
            />
          </div>

          {/* Subject line */}
          <div className="flex flex-col gap-y-[6px]">
            <label className="text-[14px] font-display font-semibold uppercase tracking-wider text-dupontBlue/60">
              Subject
            </label>
            <input
              type="text"
              className="bg-transparent border-b border-[#d4c26a]/40 px-[4px] py-[10px] font-display text-[17px] text-dupontBlue outline-none focus:border-dupontGold transition-colors placeholder:text-dupontGold/40 placeholder:italic"
              placeholder="Re: Family Gathering..."
              value={subject}
              onChange={(e) => setSubject(e.target.value.slice(0, 200))}
            />
          </div>

          {/* Message body */}
          <div className="flex flex-col gap-y-[4px]">
            <label className="text-[14px] font-display font-semibold uppercase tracking-wider text-dupontBlue/60">
              Your Letter
            </label>
            <textarea
              className="stationery-textarea"
              placeholder="Dear cousin..."
              value={content}
              onChange={(e) => {
                if (e.target.value.length <= MAX_CHARS) {
                  setContent(e.target.value);
                }
              }}
            />
            <div className={`char-counter ${charClass}`}>
              {charCount}/{MAX_CHARS}
            </div>
          </div>

          {/* Media preview */}
          {mediaUrl && (
            <div className="flex flex-row items-start gap-x-[12px]">
              <div className="letter-media-frame">
                <img src={mediaUrl} alt="Attachment" />
              </div>
              <div
                className="text-[13px] text-dupontGold cursor-pointer hover:underline mt-[4px]"
                onClick={() => {
                  setMediaUrl(null);
                  setMediaType("text");
                }}
              >
                Remove
              </div>
            </div>
          )}

          {/* Divider before actions */}
          <div
            className="h-[1px] mt-[4px]"
            style={{
              background: "linear-gradient(90deg, transparent, rgba(181, 160, 62, 0.3), transparent)",
            }}
          />

          {/* Action row */}
          <div className="flex flex-row items-center justify-between">
            <label className="text-[14px] font-body text-dupontGold cursor-pointer hover:underline">
              Attach Image or GIF
              <input
                type="file"
                accept="image/*"
                className="hidden"
                onChange={handleImageUpload}
              />
            </label>
            <SharedButton
              onClick={handleSend}
              disabled={isSending}
              loadingText={isSending ? "Sealing..." : undefined}
            >
              Seal & Send
            </SharedButton>
          </div>
        </div>

        {/* Subtle bottom gold line */}
        <div
          className="absolute bottom-0 left-[40px] right-[40px] h-[2px]"
          style={{
            background: "linear-gradient(90deg, transparent, #b5a03e, transparent)",
          }}
        />
      </div>
    </div>
  );
}

export default Compose;
